import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    replanningReviewTransformState,
    replanningRunOutputsState,
    replanningRunRegistrationsState,
    replanningRunSummaryState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import {
    ReviewTransformOption,
    reviewTransformOptions,
} from './reviewTransforms';

export interface ReviewTransforms {
    readonly options: readonly ReviewTransformOption[];
    readonly selected?: ReviewTransformOption;
    readonly select: (sopInstanceUid: string) => void;
}

// The deformable by default; a pick holds for the run it was made in.
export function useReviewTransforms(): ReviewTransforms {
    const patient = useSelectedPatient();
    // Re-rendered per changeset: a delete or an approval changes registrations in place.
    useDomainVersion();
    const runUids = useRecoilValue(replanningRunRegistrationsState);
    const summary = useRecoilValue(replanningRunSummaryState);
    const { propagatedUid } = useRecoilValue(replanningRunOutputsState);
    const [choice, setChoice] = useRecoilState(replanningReviewTransformState);

    const initRigidUid = summary?.initRigidSopInstanceUid;
    const options = reviewTransformOptions(runUids, initRigidUid, (uid) =>
        patient?.getRegistrationWithSopInstanceUid(uid)
    );

    const chosen =
        choice.forRun === propagatedUid
            ? options.find(
                  (option) => option.sopInstanceUid === choice.sopInstanceUid
              )
            : undefined;

    const select = useCallback(
        (sopInstanceUid: string) =>
            setChoice({ forRun: propagatedUid, sopInstanceUid }),
        [propagatedUid, setChoice]
    );

    return { options, selected: chosen ?? options[0], select };
}
